<script>
// NOTICE: KAPTION-INTEGRATION
export default {
  props: {
    policy: {
      type: Object,
      default: () => ({}),
    },
    isSubmitting: {
      type: Boolean,
      default: false,
    },
    submitLabel: {
      type: String,
      default: '',
    },
  },
  emits: ['submit', 'cancel'],
  data() {
    return {
      name: this.policy.name || '',
      description: this.policy.description || '',
      firstResponseValue: '',
      firstResponseUnit: 'minutes',
      nextResponseValue: '',
      nextResponseUnit: 'minutes',
      resolutionValue: '',
      resolutionUnit: 'minutes',
      mode: this.policy.mode || 'always',
      frtLabel: this.policy.frt_label || null,
      nrtLabel: this.policy.nrt_label || null,
      rtLabel: this.policy.rt_label || null,
      labelExpiryDays: this.policy.label_expiry_days ?? 7,
      showThresholdError: false,
    };
  },
  computed: {
    accountLabels() {
      return this.$store.getters['labels/getLabels'] || [];
    },
    unitOptions() {
      return [
        {
          value: 'minutes',
          label: this.$t('KAPTION_RESPONSE_POLICIES.FORM.TIME_UNITS.MINUTES'),
        },
        {
          value: 'hours',
          label: this.$t('KAPTION_RESPONSE_POLICIES.FORM.TIME_UNITS.HOURS'),
        },
        {
          value: 'days',
          label: this.$t('KAPTION_RESPONSE_POLICIES.FORM.TIME_UNITS.DAYS'),
        },
      ];
    },
    modeOptions() {
      return [
        {
          value: 'always',
          label: this.$t('KAPTION_RESPONSE_POLICIES.FORM.MODE.ALWAYS'),
          description: this.$t(
            'KAPTION_RESPONSE_POLICIES.FORM.MODE.ALWAYS_DESC'
          ),
        },
        {
          value: 'business_hours',
          label: this.$t('KAPTION_RESPONSE_POLICIES.FORM.MODE.BUSINESS_HOURS'),
          description: this.$t(
            'KAPTION_RESPONSE_POLICIES.FORM.MODE.BUSINESS_HOURS_DESC'
          ),
        },
        {
          value: 'outside_business_hours',
          label: this.$t(
            'KAPTION_RESPONSE_POLICIES.FORM.MODE.OUTSIDE_BUSINESS_HOURS'
          ),
          description: this.$t(
            'KAPTION_RESPONSE_POLICIES.FORM.MODE.OUTSIDE_BUSINESS_HOURS_DESC'
          ),
        },
      ];
    },
    hasAtLeastOneThreshold() {
      return (
        this.firstResponseValue ||
        this.nextResponseValue ||
        this.resolutionValue
      );
    },
  },
  mounted() {
    this.$store.dispatch('labels/get');
    if (this.policy.id) {
      this.parseThresholds();
    }
  },
  methods: {
    parseThresholds() {
      if (this.policy.first_response_time_threshold) {
        const parsed = this.secondsToValueUnit(
          this.policy.first_response_time_threshold
        );
        this.firstResponseValue = parsed.value;
        this.firstResponseUnit = parsed.unit;
      }
      if (this.policy.next_response_time_threshold) {
        const parsed = this.secondsToValueUnit(
          this.policy.next_response_time_threshold
        );
        this.nextResponseValue = parsed.value;
        this.nextResponseUnit = parsed.unit;
      }
      if (this.policy.resolution_time_threshold) {
        const parsed = this.secondsToValueUnit(
          this.policy.resolution_time_threshold
        );
        this.resolutionValue = parsed.value;
        this.resolutionUnit = parsed.unit;
      }
    },
    secondsToValueUnit(seconds) {
      const total = Number(seconds);
      if (total >= 86400 && total % 86400 === 0) {
        return { value: total / 86400, unit: 'days' };
      }
      if (total >= 3600 && total % 3600 === 0) {
        return { value: total / 3600, unit: 'hours' };
      }
      return { value: total / 60, unit: 'minutes' };
    },
    valueUnitToSeconds(value, unit) {
      if (!value) return null;
      const num = Number(value);
      if (unit === 'days') return num * 86400;
      if (unit === 'hours') return num * 3600;
      return num * 60;
    },
    handleSubmit() {
      if (!this.name.trim()) return;
      if (!this.hasAtLeastOneThreshold) {
        this.showThresholdError = true;
        return;
      }
      this.showThresholdError = false;

      this.$emit('submit', {
        kaption_response_policy: {
          name: this.name.trim(),
          description: this.description.trim(),
          first_response_time_threshold: this.valueUnitToSeconds(
            this.firstResponseValue,
            this.firstResponseUnit
          ),
          next_response_time_threshold: this.valueUnitToSeconds(
            this.nextResponseValue,
            this.nextResponseUnit
          ),
          resolution_time_threshold: this.valueUnitToSeconds(
            this.resolutionValue,
            this.resolutionUnit
          ),
          mode: this.mode,
          frt_label: this.frtLabel || null,
          nrt_label: this.nrtLabel || null,
          rt_label: this.rtLabel || null,
          label_expiry_days: this.labelExpiryDays,
        },
      });
    },
  },
};
</script>

<template>
  <form class="flex flex-col gap-6" @submit.prevent="handleSubmit">
    <!-- Name -->
    <div class="flex flex-col gap-1">
      <label class="text-sm font-medium text-n-slate-12">
        {{ $t('KAPTION_RESPONSE_POLICIES.FORM.NAME.LABEL') }}
      </label>
      <input
        v-model="name"
        type="text"
        class="w-full rounded-lg border border-n-weak bg-n-solid-2 px-3 py-2 text-sm text-n-slate-12 outline-none focus:border-b-solid focus:ring-1 focus:ring-b-solid"
        :placeholder="$t('KAPTION_RESPONSE_POLICIES.FORM.NAME.PLACEHOLDER')"
        required
      />
    </div>

    <!-- Description -->
    <div class="flex flex-col gap-1">
      <label class="text-sm font-medium text-n-slate-12">
        {{ $t('KAPTION_RESPONSE_POLICIES.FORM.DESCRIPTION.LABEL') }}
      </label>
      <textarea
        v-model="description"
        rows="2"
        class="w-full rounded-lg border border-n-weak bg-n-solid-2 px-3 py-2 text-sm text-n-slate-12 outline-none focus:border-b-solid focus:ring-1 focus:ring-b-solid resize-none"
        :placeholder="
          $t('KAPTION_RESPONSE_POLICIES.FORM.DESCRIPTION.PLACEHOLDER')
        "
      />
    </div>

    <!-- Thresholds -->
    <div class="flex flex-col gap-4">
      <h4 class="text-sm font-semibold text-n-slate-12">
        {{ $t('KAPTION_RESPONSE_POLICIES.FORM.THRESHOLDS_TITLE') }}
      </h4>

      <!-- First Response Time -->
      <div class="flex flex-col gap-1">
        <label class="text-xs font-medium text-n-slate-11">
          {{ $t('KAPTION_RESPONSE_POLICIES.FORM.FIRST_RESPONSE_TIME.LABEL') }}
        </label>
        <div class="flex gap-2">
          <input
            v-model.number="firstResponseValue"
            type="number"
            min="1"
            class="w-24 rounded-lg border border-n-weak bg-n-solid-2 px-3 py-2 text-sm text-n-slate-12 outline-none focus:border-b-solid focus:ring-1 focus:ring-b-solid"
            :placeholder="
              $t(
                'KAPTION_RESPONSE_POLICIES.FORM.FIRST_RESPONSE_TIME.PLACEHOLDER'
              )
            "
          />
          <select
            v-model="firstResponseUnit"
            class="rounded-lg border border-n-weak bg-n-solid-2 px-3 py-2 text-sm text-n-slate-12 outline-none focus:border-b-solid focus:ring-1 focus:ring-b-solid"
          >
            <option
              v-for="opt in unitOptions"
              :key="opt.value"
              :value="opt.value"
            >
              {{ opt.label }}
            </option>
          </select>
        </div>
      </div>

      <!-- Next Response Time -->
      <div class="flex flex-col gap-1">
        <label class="text-xs font-medium text-n-slate-11">
          {{ $t('KAPTION_RESPONSE_POLICIES.FORM.NEXT_RESPONSE_TIME.LABEL') }}
        </label>
        <div class="flex gap-2">
          <input
            v-model.number="nextResponseValue"
            type="number"
            min="1"
            class="w-24 rounded-lg border border-n-weak bg-n-solid-2 px-3 py-2 text-sm text-n-slate-12 outline-none focus:border-b-solid focus:ring-1 focus:ring-b-solid"
            :placeholder="
              $t(
                'KAPTION_RESPONSE_POLICIES.FORM.NEXT_RESPONSE_TIME.PLACEHOLDER'
              )
            "
          />
          <select
            v-model="nextResponseUnit"
            class="rounded-lg border border-n-weak bg-n-solid-2 px-3 py-2 text-sm text-n-slate-12 outline-none focus:border-b-solid focus:ring-1 focus:ring-b-solid"
          >
            <option
              v-for="opt in unitOptions"
              :key="opt.value"
              :value="opt.value"
            >
              {{ opt.label }}
            </option>
          </select>
        </div>
      </div>

      <!-- Resolution Time -->
      <div class="flex flex-col gap-1">
        <label class="text-xs font-medium text-n-slate-11">
          {{ $t('KAPTION_RESPONSE_POLICIES.FORM.RESOLUTION_TIME.LABEL') }}
        </label>
        <div class="flex gap-2">
          <input
            v-model.number="resolutionValue"
            type="number"
            min="1"
            class="w-24 rounded-lg border border-n-weak bg-n-solid-2 px-3 py-2 text-sm text-n-slate-12 outline-none focus:border-b-solid focus:ring-1 focus:ring-b-solid"
            :placeholder="
              $t('KAPTION_RESPONSE_POLICIES.FORM.RESOLUTION_TIME.PLACEHOLDER')
            "
          />
          <select
            v-model="resolutionUnit"
            class="rounded-lg border border-n-weak bg-n-solid-2 px-3 py-2 text-sm text-n-slate-12 outline-none focus:border-b-solid focus:ring-1 focus:ring-b-solid"
          >
            <option
              v-for="opt in unitOptions"
              :key="opt.value"
              :value="opt.value"
            >
              {{ opt.label }}
            </option>
          </select>
        </div>
      </div>

      <p v-if="showThresholdError" class="text-xs text-r-solid">
        {{ $t('KAPTION_RESPONSE_POLICIES.FORM.THRESHOLD_ERROR') }}
      </p>
    </div>

    <!-- Labels on SLA Miss -->
    <div v-if="hasAtLeastOneThreshold" class="flex flex-col gap-4">
      <div class="flex flex-col gap-1">
        <h4 class="text-sm font-semibold text-n-slate-12">
          {{ $t('KAPTION_RESPONSE_POLICIES.FORM.LABELS_TITLE') }}
        </h4>
        <p class="text-xs text-n-slate-11">
          {{ $t('KAPTION_RESPONSE_POLICIES.FORM.LABELS_DESCRIPTION') }}
        </p>
      </div>

      <div v-if="firstResponseValue" class="flex flex-col gap-1">
        <label class="text-xs font-medium text-n-slate-11">
          {{ $t('KAPTION_RESPONSE_POLICIES.FORM.FRT_LABEL.LABEL') }}
        </label>
        <select
          v-model="frtLabel"
          class="rounded-lg border border-n-weak bg-n-solid-2 px-3 py-2 text-sm text-n-slate-12 outline-none focus:border-b-solid focus:ring-1 focus:ring-b-solid"
        >
          <option :value="null">
            {{ $t('KAPTION_RESPONSE_POLICIES.FORM.NO_LABEL') }}
          </option>
          <option
            v-for="label in accountLabels"
            :key="label.id"
            :value="label.title"
          >
            {{ label.title }}
          </option>
        </select>
      </div>

      <div v-if="nextResponseValue" class="flex flex-col gap-1">
        <label class="text-xs font-medium text-n-slate-11">
          {{ $t('KAPTION_RESPONSE_POLICIES.FORM.NRT_LABEL.LABEL') }}
        </label>
        <select
          v-model="nrtLabel"
          class="rounded-lg border border-n-weak bg-n-solid-2 px-3 py-2 text-sm text-n-slate-12 outline-none focus:border-b-solid focus:ring-1 focus:ring-b-solid"
        >
          <option :value="null">
            {{ $t('KAPTION_RESPONSE_POLICIES.FORM.NO_LABEL') }}
          </option>
          <option
            v-for="label in accountLabels"
            :key="label.id"
            :value="label.title"
          >
            {{ label.title }}
          </option>
        </select>
      </div>

      <div v-if="resolutionValue" class="flex flex-col gap-1">
        <label class="text-xs font-medium text-n-slate-11">
          {{ $t('KAPTION_RESPONSE_POLICIES.FORM.RT_LABEL.LABEL') }}
        </label>
        <select
          v-model="rtLabel"
          class="rounded-lg border border-n-weak bg-n-solid-2 px-3 py-2 text-sm text-n-slate-12 outline-none focus:border-b-solid focus:ring-1 focus:ring-b-solid"
        >
          <option :value="null">
            {{ $t('KAPTION_RESPONSE_POLICIES.FORM.NO_LABEL') }}
          </option>
          <option
            v-for="label in accountLabels"
            :key="label.id"
            :value="label.title"
          >
            {{ label.title }}
          </option>
        </select>
      </div>
    </div>

    <!-- Label Expiry -->
    <div v-if="hasAtLeastOneThreshold" class="flex flex-col gap-1">
      <label class="text-xs font-medium text-n-slate-11">
        {{ $t('KAPTION_RESPONSE_POLICIES.FORM.LABEL_EXPIRY.LABEL') }}
      </label>
      <div class="flex items-center gap-2">
        <input
          v-model.number="labelExpiryDays"
          type="number"
          min="0"
          class="w-24 rounded-lg border border-n-weak bg-n-solid-2 px-3 py-2 text-sm text-n-slate-12 outline-none focus:border-b-solid focus:ring-1 focus:ring-b-solid"
          :placeholder="
            $t('KAPTION_RESPONSE_POLICIES.FORM.LABEL_EXPIRY.PLACEHOLDER')
          "
        />
        <span class="text-xs text-n-slate-11">
          {{ $t('KAPTION_RESPONSE_POLICIES.FORM.TIME_UNITS.DAYS') }}
        </span>
      </div>
      <p class="text-xs text-n-slate-11">
        {{ $t('KAPTION_RESPONSE_POLICIES.FORM.LABEL_EXPIRY.HELP') }}
      </p>
    </div>

    <!-- Evaluation Mode -->
    <div class="flex flex-col gap-3">
      <h4 class="text-sm font-semibold text-n-slate-12">
        {{ $t('KAPTION_RESPONSE_POLICIES.FORM.MODE.LABEL') }}
      </h4>
      <div class="flex flex-col gap-2">
        <label
          v-for="opt in modeOptions"
          :key="opt.value"
          class="flex items-start gap-3 rounded-lg border border-n-weak p-3 cursor-pointer transition-colors"
          :class="
            mode === opt.value
              ? 'border-b-solid bg-b-alpha-1'
              : 'hover:bg-n-alpha-2'
          "
        >
          <input
            v-model="mode"
            type="radio"
            name="policy-mode"
            :value="opt.value"
            class="mt-0.5 size-4 text-b-solid focus:ring-b-solid"
          />
          <div class="flex flex-col">
            <span class="text-sm font-medium text-n-slate-12">
              {{ opt.label }}
            </span>
            <span class="text-xs text-n-slate-11">
              {{ opt.description }}
            </span>
          </div>
        </label>
      </div>
    </div>

    <!-- Actions -->
    <div class="flex items-center gap-2 pt-2">
      <button
        type="submit"
        :disabled="isSubmitting"
        class="rounded-lg bg-b-solid px-4 py-2 text-sm font-medium text-white hover:bg-b-solid-hover disabled:opacity-50"
      >
        {{ submitLabel || $t('KAPTION_RESPONSE_POLICIES.FORM.SUBMIT') }}
      </button>
      <button
        type="button"
        class="rounded-lg border border-n-weak px-4 py-2 text-sm font-medium text-n-slate-12 hover:bg-n-alpha-2"
        @click="$emit('cancel')"
      >
        {{ $t('KAPTION_RESPONSE_POLICIES.FORM.CANCEL') }}
      </button>
    </div>
  </form>
</template>
